import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Check, X, Edit3, AlertTriangle, Target } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DIFFICULTY = [
  { v: "prudente", l: "Prudente", c: "bg-emerald-100 text-emerald-800" },
  { v: "realistico", l: "Realistico", c: "bg-blue-100 text-blue-800" },
  { v: "ambizioso", l: "Ambizioso", c: "bg-red-100 text-red-800" },
];
const RELIABILITY = { basso: "bg-red-50 text-red-700", medio: "bg-amber-50 text-amber-700", alto: "bg-emerald-50 text-emerald-700" };

export default function AIGoalDialog({ athleteId, testId, testName, testUnit, higherIsBetter, currentValue, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState({});

  const compute = async () => {
    setLoading(true); setProposal(null); setEditing(false);
    try {
      const { data } = await api.post("/ai/test-goal", { athlete_id: athleteId, test_id: testId, target_days: 56 });
      setProposal(data);
      setEdits({
        final_value: data.ai_proposed_value,
        final_target_date: data.ai_target_date,
        final_difficulty: data.ai_difficulty,
        final_motivation: data.ai_motivation,
        priority: "media",
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore AI"); }
    finally { setLoading(false); }
  };

  const openAndFetch = () => { setOpen(true); compute(); };

  const save = async (status = "approved") => {
    if (!proposal) return;
    setLoading(true);
    try {
      const payload = { ...proposal, ...edits, athlete_id: athleteId, test_id: testId, status };
      await api.post("/test-goals", payload);
      toast.success(status === "approved" ? "Obiettivo approvato" : status === "modified" ? "Obiettivo modificato e salvato" : "Obiettivo rifiutato");
      setOpen(false); setProposal(null); setEditing(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };

  const reject = async () => {
    if (!window.confirm("Rifiutare la proposta AI?")) return;
    save("rejected");
  };

  const diffColor = DIFFICULTY.find((d) => d.v === (editing ? edits.final_difficulty : proposal?.ai_difficulty))?.c;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setProposal(null); setEditing(false); } }}>
      <div onClick={openAndFetch}>{trigger || <Button size="sm" variant="outline" data-testid="ai-goal-btn"><Sparkles className="w-3.5 h-3.5 mr-1" />Obiettivo AI</Button>}</div>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#0052FF]" />Proposta AI · {testName}</DialogTitle>
          <DialogDescription>Obiettivo calcolato su statistiche personali dell'atleta. Modifica o approva prima di renderlo visibile.</DialogDescription>
        </DialogHeader>

        {loading && <div className="py-10 text-center text-zinc-500"><Sparkles className="w-8 h-8 mx-auto mb-2 animate-pulse text-[#0052FF]" />Analisi in corso…</div>}

        {proposal && (
          <div className="space-y-4 py-2">
            {proposal.ai_warning && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-900">{proposal.ai_warning}</div>
              </div>
            )}

            {proposal.stats && (
              <div className="grid grid-cols-4 gap-2 text-center bg-zinc-50 border border-zinc-200 rounded-md p-3">
                <div><div className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono">Ultimo</div><div className="font-mono text-base font-semibold">{proposal.stats.latest} <span className="text-[10px] text-zinc-500">{testUnit}</span></div></div>
                <div><div className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono">Precedente</div><div className="font-mono text-base">{proposal.stats.prev ?? "—"}</div></div>
                <div><div className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono">Best</div><div className="font-mono text-base font-semibold text-emerald-700">{proposal.stats.best}</div></div>
                <div><div className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono">Media recente</div><div className="font-mono text-base">{proposal.stats.recent_avg?.toFixed(2)}</div></div>
              </div>
            )}

            <div className="bg-white border-2 border-black rounded-md p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-black" />
                <span className="font-heading font-semibold">Obiettivo proposto</span>
                <span className={`ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${diffColor}`}>{editing ? edits.final_difficulty : proposal.ai_difficulty}</span>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${RELIABILITY[proposal.ai_reliability]}`}>affidabilità {proposal.ai_reliability}</span>
              </div>

              {!editing ? (
                <>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono">Valore</div>
                      <div className="font-mono text-3xl font-bold">{proposal.ai_proposed_value} <span className="text-sm text-zinc-500 font-normal">{testUnit}</span></div>
                      <div className="text-[10px] text-zinc-500 font-mono">range {proposal.ai_range_low}—{proposal.ai_range_high}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono">Miglioramento</div>
                      <div className="font-mono text-2xl font-bold text-[#0052FF]">{proposal.ai_pct_change > 0 ? "+" : ""}{proposal.ai_pct_change}%</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono">Entro</div>
                      <div className="font-mono text-base font-semibold">{proposal.ai_target_date}</div>
                    </div>
                  </div>
                  {proposal.ai_motivation && (
                    <div className="text-sm text-zinc-700 bg-zinc-50 rounded p-3 border border-zinc-100 italic">"{proposal.ai_motivation}"</div>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Valore obiettivo ({testUnit})</Label><Input type="number" step="0.01" value={edits.final_value} onChange={(e) => setEdits({...edits, final_value: parseFloat(e.target.value) || 0})} data-testid="goal-final-value" /></div>
                    <div><Label className="text-xs">Data scadenza</Label><Input type="date" value={edits.final_target_date} onChange={(e) => setEdits({...edits, final_target_date: e.target.value})} /></div>
                    <div><Label className="text-xs">Difficoltà</Label>
                      <Select value={edits.final_difficulty} onValueChange={(v) => setEdits({...edits, final_difficulty: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{DIFFICULTY.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Priorità</Label>
                      <Select value={edits.priority} onValueChange={(v) => setEdits({...edits, priority: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="bassa">Bassa</SelectItem><SelectItem value="media">Media</SelectItem><SelectItem value="alta">Alta</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label className="text-xs">Motivazione</Label><Textarea rows={2} value={edits.final_motivation} onChange={(e) => setEdits({...edits, final_motivation: e.target.value})} /></div>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={reject} disabled={!proposal || loading} className="text-red-600 border-red-200"><X className="w-3.5 h-3.5 mr-1" />Rifiuta</Button>
          {!editing && <Button variant="outline" onClick={() => setEditing(true)} disabled={!proposal || loading} data-testid="goal-edit-btn"><Edit3 className="w-3.5 h-3.5 mr-1" />Modifica</Button>}
          {editing && <Button variant="outline" onClick={() => setEditing(false)}>Annulla modifiche</Button>}
          <Button onClick={() => save(editing ? "modified" : "approved")} disabled={!proposal || loading} data-testid="goal-approve-btn" className="bg-black hover:bg-zinc-800">
            <Check className="w-3.5 h-3.5 mr-1" />{editing ? "Salva modifiche" : "Approva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
